import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../api";
import { getSocket } from "../socket";
import MessageBubble from "./MessageBubble";
import InputArea from "./InputArea";

function formatLastSeen(iso) {
  if (!iso) return "Offline";
  const date = new Date(iso);
  const now  = new Date();
  const diff  = Math.floor((now - date) / 1000); // secondi

  if (diff < 60)   return "ultimo accesso pochi secondi fa";
  if (diff < 3600) return `ultimo accesso ${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) {
    return `ultimo accesso alle ${date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
  }
  return `ultimo accesso il ${date.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}`;
}

export default function Chat({ session, onLogout, onOpenAdmin }) {
  const { token, user } = session;

  const [users, setUsers]         = useState([]);
  const [partner, setPartner]     = useState(null);
  const [messages, setMessages]   = useState([]);
  const [onlineIds, setOnlineIds] = useState([]);
  const [lastSeen, setLastSeen]   = useState({});  // { userId: isoString }
  const [typing, setTyping]       = useState(false);
  const [sidebarOpen, setSidebar] = useState(true);

  const bottomRef   = useRef(null);
  const socketRef   = useRef(null);
  const partnerRef  = useRef(null); // ref per usarlo nei callback socket
  const typingTimer = useRef(null);

  // Tieni partnerRef sincronizzato
  useEffect(() => { partnerRef.current = partner; }, [partner]);

  // ── Socket setup ────────────────────────────────────────────────────────────
  useEffect(() => {
    const socket = getSocket(token);
    socketRef.current = socket;

    socket.on("users:online", (ids) => setOnlineIds(ids));

    socket.on("user:online", ({ userId, online, lastSeenAt }) => {
      setOnlineIds((prev) =>
        online ? [...new Set([...prev, userId])] : prev.filter((id) => id !== userId)
      );
      if (!online && lastSeenAt) {
        setLastSeen((prev) => ({ ...prev, [userId]: lastSeenAt }));
      }
    });

    socket.on("message:new", (msg) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        const next = [...prev, msg];
        // Se il messaggio è del partner corrente, segnalo come letto immediatamente
        if (partnerRef.current && msg.senderId === partnerRef.current.id) {
          socket.emit("messages:read", { senderId: msg.senderId });
        }
        return next;
      });
    });

    // Aggiorna readAt dei messaggi inviati da me quando il partner li legge
    socket.on("messages:read", ({ readBy, readAt }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.senderId === user.id && m.receiverId === readBy && !m.readAt
            ? { ...m, readAt }
            : m
        )
      );
    });

    socket.on("message:deleted", ({ messageId }) => {
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    });

    socket.on("typing:start", ({ userId: tid }) => {
      if (partnerRef.current?.id === tid) setTyping(true);
    });
    socket.on("typing:stop", ({ userId: tid }) => {
      if (partnerRef.current?.id === tid) setTyping(false);
    });

    return () => {
      socket.off("users:online");
      socket.off("user:online");
      socket.off("message:new");
      socket.off("messages:read");
      socket.off("message:deleted");
      socket.off("typing:start");
      socket.off("typing:stop");
    };
  }, [token, user.id]);

  // ── Carica lista utenti con lastSeenAt ──────────────────────────────────────
  useEffect(() => {
    api.getUsers().then((list) => {
      setUsers(list);
      const seen = {};
      list.forEach((u) => { if (u.lastSeenAt) seen[u.id] = u.lastSeenAt; });
      setLastSeen(seen);
    }).catch(console.error);
  }, []);

  // ── Carica cronologia e segna letti quando cambia il partner ─────────────────
  useEffect(() => {
    if (!partner) return;
    setMessages([]);
    setTyping(false);
    api.getMessages(partner.id).then((msgs) => {
      setMessages(msgs);
      // Segna come letti i messaggi del partner appena carico la cronologia
      socketRef.current?.emit("messages:read", { senderId: partner.id });
    }).catch(console.error);
  }, [partner]);

  // ── Scroll automatico ────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Invio ────────────────────────────────────────────────────────────────────
  const handleDelete = useCallback((messageId) => {
    socketRef.current?.emit("message:delete", { messageId }, (ack) => {
      if (ack?.error) console.error(ack.error);
    });
  }, []);

  const handleSend = useCallback((payload) => {
    if (!partnerRef.current) return;
    socketRef.current?.emit(
      "message:send",
      { receiverId: partnerRef.current.id, ...payload },
      (ack) => { if (ack?.error) console.error(ack.error); }
    );
  }, []);

  const handleTypingStart = useCallback(() => {
    if (!partnerRef.current) return;
    socketRef.current?.emit("typing:start", { receiverId: partnerRef.current.id });
  }, []);

  const handleTypingStop = useCallback(() => {
    if (!partnerRef.current) return;
    socketRef.current?.emit("typing:stop", { receiverId: partnerRef.current.id });
  }, []);

  useEffect(() => () => clearTimeout(typingTimer.current), []);

  const isOnline = (id) => onlineIds.includes(id);

  function partnerStatus() {
    if (!partner) return "";
    if (typing) return "sta scrivendo…";
    if (isOnline(partner.id)) return "Online";
    return formatLastSeen(lastSeen[partner.id]);
  }

  return (
    <div className="chat-app">
      {/* ── Sidebar ── */}
      <aside className={`sidebar ${sidebarOpen ? "open" : "closed"}`}>
        <div className="sidebar-header">
          <div className="my-avatar">{user.username[0].toUpperCase()}</div>
          <span className="my-name">{user.username}</span>
          {onOpenAdmin && (
            <button className="icon-btn admin-btn" onClick={onOpenAdmin} title="Pannello admin">⚙</button>
          )}
          <button className="icon-btn logout-btn" onClick={onLogout} title="Esci">⏻</button>
        </div>

        <div className="contacts">
          {users.length === 0 && <p className="no-contacts">Nessun altro utente.</p>}
          {users.map((u) => (
            <button
              key={u.id}
              className={`contact-item ${partner?.id === u.id ? "active" : ""}`}
              onClick={() => {
                setPartner(u);
                if (window.innerWidth < 768) setSidebar(false);
              }}
            >
              <div className="contact-avatar">
                {u.username[0].toUpperCase()}
                {isOnline(u.id) && <span className="online-dot" />}
              </div>
              <div className="contact-info">
                <span className="contact-name">{u.username}</span>
                <span className="contact-status">
                  {isOnline(u.id) ? "Online" : formatLastSeen(lastSeen[u.id])}
                </span>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* ── Area chat ── */}
      <main className="chat-main">
        {!partner ? (
          <div className="no-chat">
            <div className="no-chat-icon">💬</div>
            <h2>Seleziona un contatto</h2>
            <p>Scegli con chi vuoi chattare dalla barra laterale.</p>
          </div>
        ) : (
          <>
            <div className="chat-header">
              <button className="icon-btn back-btn" onClick={() => setSidebar(true)}>‹</button>
              <div className="chat-avatar">{partner.username[0].toUpperCase()}</div>
              <div className="chat-header-info">
                <span className="chat-partner-name">{partner.username}</span>
                <span className={`chat-partner-status ${typing ? "typing" : ""}`}>
                  {partnerStatus()}
                </span>
              </div>
            </div>

            <div className="messages-list">
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  isMine={msg.senderId === user.id}
                  onDelete={handleDelete}
                />
              ))}
              {typing && (
                <div className="typing-indicator"><span /><span /><span /></div>
              )}
              <div ref={bottomRef} />
            </div>

            <InputArea
              onSend={handleSend}
              onTypingStart={handleTypingStart}
              onTypingStop={handleTypingStop}
            />
          </>
        )}
      </main>
    </div>
  );
}
